import React, { useEffect, useMemo, useState } from "react";
import { List, useTable, EditButton, ShowButton, DeleteButton } from "@refinedev/antd";
import type { BaseRecord, CrudFilters } from "@refinedev/core";
import {
  Table,
  Space,
  Image,
  Input,
  Segmented,
  Tag,
  Typography,
  Button,
  Form,
  DatePicker,
  InputNumber,
  Divider,
  Tooltip,
  App,
  Card,
} from "antd";
import { PlusOutlined, ReloadOutlined } from "@ant-design/icons";
import type { SegmentedValue } from "antd/es/segmented";
import { useLocation, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { supabaseClient } from "../../utility/supabaseClient";
import { getStatusMeta } from "./shared";

type MovieRecord = BaseRecord & {
  poster_url?: string | null;
  backdrop_url?: string | null;
  video_url?: string | null;
  movies_videos?: { id?: string; status?: string | null; cloudflare_uid?: string | null }[];
  title?: string;
  original_title?: string | null;
  release_date?: string | null;
  runtime?: number | null;
  vote_average?: number | null;
  popularity?: number | null;
  view_count?: number | null;
  status?: string | null;
  preview_duration?: number | null;
  updated_at?: string | null;
  language?: string | null;
  movie_categories?: { categories?: { name?: string | null } }[];
};

const STATUS_SEGMENTS = [
  { label: "全部状态", value: "all" },
  { label: "已发布", value: "released" },
  { label: "草稿", value: "draft" },
  { label: "已归档", value: "archived" },
];

const { RangePicker } = DatePicker;

export const MovieList: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const searchFromURL = params.get("search") ?? "";
  const statusFromURL = params.get("status") ?? "all";
  const { message } = App.useApp();

  const { tableProps, setFilters, tableQueryResult } = useTable<MovieRecord>({
    syncWithLocation: true,
    sorters: {
      initial: [{ field: "updated_at", order: "desc" }],
    },
    meta: {
      select: "*, movie_categories(categories(name)), movies_videos(id, status, cloudflare_uid)",
    },
  });

  const [searchValue, setSearchValue] = useState(searchFromURL);
  const [statusValue, setStatusValue] = useState(statusFromURL);
  const [releaseRange, setReleaseRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null] | null>(null);
  const [scoreMin, setScoreMin] = useState<number | undefined>();
  const [scoreMax, setScoreMax] = useState<number | undefined>();
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [bulkLoading, setBulkLoading] = useState(false);

  useEffect(() => {
    setSearchValue(searchFromURL);
    setStatusValue(statusFromURL);
  }, [searchFromURL, statusFromURL]);

  const syncToUrl = (nextSearch: string, nextStatus: string) => {
    const nextParams = new URLSearchParams(location.search);
    if (nextSearch) {
      nextParams.set("search", nextSearch);
    } else {
      nextParams.delete("search");
    }
    if (nextStatus && nextStatus !== "all") {
      nextParams.set("status", nextStatus);
    } else {
      nextParams.delete("status");
    }
    const queryString = nextParams.toString();
    navigate({
      pathname: "/movies",
      search: queryString ? `?${queryString}` : "",
    });
  };

  const applyFilters = (options?: {
    search?: string;
    status?: string;
    release?: [dayjs.Dayjs | null, dayjs.Dayjs | null] | null;
    minScore?: number;
    maxScore?: number;
  }) => {
    const nextSearch = options?.search ?? searchValue;
    const nextStatus = options?.status ?? statusValue;
    const nextRelease = options?.release ?? releaseRange;
    const nextMinScore = options?.minScore ?? scoreMin;
    const nextMaxScore = options?.maxScore ?? scoreMax;

    const nextFilters: CrudFilters = [];

    if (nextSearch) {
      nextFilters.push({
        field: "title",
        operator: "contains",
        value: nextSearch,
      });
    }

    if (nextStatus && nextStatus !== "all") {
      nextFilters.push({
        field: "status",
        operator: "eq",
        value: nextStatus,
      });
    }

    if (nextRelease?.[0]) {
      nextFilters.push({
        field: "release_date",
        operator: "gte",
        value: nextRelease[0].format("YYYY-MM-DD"),
      });
    }

    if (nextRelease?.[1]) {
      nextFilters.push({
        field: "release_date",
        operator: "lte",
        value: nextRelease[1].format("YYYY-MM-DD"),
      });
    }

    if (typeof nextMinScore === "number") {
      nextFilters.push({
        field: "vote_average",
        operator: "gte",
        value: nextMinScore,
      });
    }

    if (typeof nextMaxScore === "number") {
      nextFilters.push({
        field: "vote_average",
        operator: "lte",
        value: nextMaxScore,
      });
    }

    syncToUrl(nextSearch, nextStatus);
    setFilters(nextFilters, "replace");
  };

  const handleSearch = (value: string) => {
    const trimmed = value.trim();
    setSearchValue(trimmed);
    applyFilters({ search: trimmed });
  };

  const handleStatusChange = (value: SegmentedValue) => {
    const nextValue = String(value);
    setStatusValue(nextValue);
    applyFilters({ status: nextValue });
  };

  const handleResetFilters = () => {
    setSearchValue("");
    setStatusValue("all");
    setReleaseRange(null);
    setScoreMin(undefined);
    setScoreMax(undefined);
    setSelectedRowKeys([]);
    navigate({ pathname: "/movies", search: "" });
    setFilters([], "replace");
  };

  const categoryMap = useMemo(
    () =>
      (tableProps?.dataSource || []).reduce<Record<string, string[]>>((acc, record) => {
        const categories = (record as MovieRecord)?.movie_categories
          ?.map((item) => item?.categories?.name)
          .filter(Boolean) as string[];
        if (record.id && categories?.length) {
          acc[record.id as string] = categories;
        }
        return acc;
      }, {}),
    [tableProps?.dataSource]
  );

  const mediaHealth = (record: MovieRecord) => {
    const hasVideo = Boolean(record.video_url) || Boolean(record.movies_videos?.length);
    const items = [
      record.poster_url ? (
        <Tag key="poster" color="green">
          封面已传
        </Tag>
      ) : (
        <Tag key="poster" color="red">
          缺少封面
        </Tag>
      ),
      record.backdrop_url ? (
        <Tag key="backdrop" color="blue">
          竖图就绪
        </Tag>
      ) : (
        <Tag key="backdrop" color="red">
          缺少竖图
        </Tag>
      ),
      hasVideo ? (
        <Tag key="video" color="green">
          视频已上传
        </Tag>
      ) : (
        <Tag key="video" color="orange">
          无视频
        </Tag>
      ),
    ];
    return <Space size={[4, 4]} wrap>{items}</Space>;
  };

  const handleBulkStatus = async (nextStatus: string) => {
    if (!selectedRowKeys.length) {
      message.warning("请先勾选需要变更的影片");
      return;
    }
    try {
      setBulkLoading(true);
      const { error } = await supabaseClient
        .from("movies")
        .update({ status: nextStatus })
        .in(
          "id",
          selectedRowKeys.map(String)
        );
      if (error) throw error;
      message.success("状态批量更新完成");
      setSelectedRowKeys([]);
      tableQueryResult?.refetch?.();
    } catch (err: any) {
      console.error("批量更新状态失败:", err);
      message.error(err?.message || "批量更新失败，请稍后重试");
    } finally {
      setBulkLoading(false);
    }
  };

  return (
    <List
      title="影片管理"
      headerButtons={() => null}
    >
      <Card
        bordered={false}
        style={{ marginBottom: 12 }}
        bodyStyle={{ paddingBottom: 4 }}
      >
        <Space size="middle" wrap align="start" style={{ width: "100%", justifyContent: "space-between" }}>
          <Space size="middle" wrap>
            <Space.Compact style={{ width: 320 }}>
              <Input
                allowClear
                placeholder="输入片名 / 原标题快速检索"
                value={searchValue}
                onChange={(event) => setSearchValue(event.target.value)}
                onPressEnter={() => handleSearch(searchValue)}
              />
              <Button type="primary" onClick={() => handleSearch(searchValue)}>
                搜索
              </Button>
            </Space.Compact>
            <Segmented
              options={STATUS_SEGMENTS}
              value={statusValue}
              onChange={handleStatusChange}
            />
          </Space>

          <Space>
            <Button
              icon={<ReloadOutlined />}
              onClick={handleResetFilters}
            >
              重置筛选
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => navigate("/movies/create")}
            >
              新建影片
            </Button>
          </Space>
        </Space>

        <Divider style={{ margin: "12px 0" }} />

        <Form layout="inline" style={{ gap: 12, flexWrap: "wrap" }}>
          <Form.Item label="上映时间">
            <RangePicker
              value={releaseRange as any}
              onChange={(value) => {
                setReleaseRange(value);
                applyFilters({ release: value });
              }}
              placeholder={["开始日期", "结束日期"]}
              allowClear
            />
          </Form.Item>
          <Form.Item label="评分区间">
            <Space.Compact>
              <InputNumber
                min={0}
                max={10}
                step={0.1}
                style={{ width: 90 }}
                placeholder="最小"
                value={scoreMin}
                onChange={(value) => {
                  const numeric = typeof value === "number" ? value : undefined;
                  setScoreMin(numeric);
                  applyFilters({ minScore: numeric });
                }}
              />
              <Input
                disabled
                style={{ width: 20, textAlign: "center" }}
                value="-"
              />
              <InputNumber
                min={0}
                max={10}
                step={0.1}
                style={{ width: 90 }}
                placeholder="最大"
                value={scoreMax}
                onChange={(value) => {
                  const numeric = typeof value === "number" ? value : undefined;
                  setScoreMax(numeric);
                  applyFilters({ maxScore: numeric });
                }}
              />
            </Space.Compact>
          </Form.Item>
        </Form>
      </Card>

      <Card
        bordered={false}
        style={{ marginBottom: 12 }}
        bodyStyle={{ padding: 12 }}
      >
        <Space size="small" wrap>
          <Button
            disabled={!selectedRowKeys.length}
            loading={bulkLoading}
            onClick={() => handleBulkStatus("released")}
          >
            批量发布
          </Button>
          <Button
            disabled={!selectedRowKeys.length}
            loading={bulkLoading}
            onClick={() => handleBulkStatus("draft")}
          >
            批量改为草稿
          </Button>
          <Button
            danger
            disabled={!selectedRowKeys.length}
            loading={bulkLoading}
            onClick={() => handleBulkStatus("archived")}
          >
            批量归档
          </Button>
          <Typography.Text type="secondary">
            已选 {selectedRowKeys.length} 个影片
          </Typography.Text>
        </Space>
      </Card>

      <Table
        {...tableProps}
        rowKey="id"
        size="middle"
        scroll={{ x: 1200 }}
        rowSelection={{
          selectedRowKeys,
          onChange: (keys) => setSelectedRowKeys(keys),
        }}
      >
        <Table.Column
          dataIndex="poster_url"
          title="封面"
          width={160}
          render={(value: string) => (
            <div
              style={{
                width: 140,
                aspectRatio: "16 / 9",
                borderRadius: 8,
                overflow: "hidden",
                background: "#0f172a",
                display: "flex",
              }}
            >
              <Image
                src={value || "https://placehold.co/640x360?text=No+Image"}
                alt="海报"
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
                preview={value ? { mask: "预览" } : false}
                fallback="https://placehold.co/640x360?text=封面加载失败"
              />
            </div>
          )}
        />
        <Table.Column
          dataIndex="title"
          title="影片"
          width={280}
          render={(_, record: MovieRecord) => {
            const categories = categoryMap[record.id as string] || [];
            return (
              <Space direction="vertical" size={4} style={{ width: "100%" }}>
                <Typography.Text strong ellipsis style={{ maxWidth: 220 }}>
                  {record.title || "未命名"}
                </Typography.Text>
                {record.original_title && (
                  <Typography.Text type="secondary" ellipsis style={{ maxWidth: 220 }}>
                    {record.original_title}
                  </Typography.Text>
                )}
                <Space size={[4, 4]} wrap>
                  {categories.length ? (
                    categories.map((name) => (
                      <Tag key={name} color="blue">
                        {name}
                      </Tag>
                    ))
                  ) : (
                    <Tag color="default">未分组</Tag>
                  )}
                </Space>
              </Space>
            );
          }}
        />
        <Table.Column
          dataIndex="release_date"
          title="上映 / 时长"
          width={180}
          render={(value: string | null, record: MovieRecord) => (
            <Space direction="vertical" size={4}>
              <Typography.Text>
                {value ? dayjs(value).format("YYYY-MM-DD") : "未设置"}
              </Typography.Text>
              <Typography.Text type="secondary">
                {record.runtime ? `${record.runtime} 分钟` : "时长未知"}
              </Typography.Text>
            </Space>
          )}
        />
        <Table.Column
          dataIndex="vote_average"
          title="评分 / 热度"
          width={150}
          render={(_, record: MovieRecord) => (
            <Space direction="vertical" size={2}>
              <Typography.Text strong>
                {typeof record.vote_average === "number"
                  ? record.vote_average.toFixed(1)
                  : "暂无"}
              </Typography.Text>
              <Typography.Text type="secondary">
                人气 {record.popularity ?? 0} · 播放 {record.view_count ?? 0}
              </Typography.Text>
            </Space>
          )}
        />
        <Table.Column
          dataIndex="preview_duration"
          title="试看/语言"
          width={140}
          render={(value: number | null, record: MovieRecord) => (
            <Space direction="vertical" size={2}>
              <Typography.Text>
                {value ? `${value} 秒` : "未设置试看"}
              </Typography.Text>
              <Typography.Text type="secondary">{record.language || "未设置语言"}</Typography.Text>
            </Space>
          )}
        />
        <Table.Column
          title="媒资状态"
          width={220}
          render={(_, record: MovieRecord) => mediaHealth(record)}
        />
        <Table.Column
          dataIndex="status"
          title="上线状态"
          width={120}
          render={(value?: string) => {
            const statusMeta = getStatusMeta(value);
            return <Tag color={statusMeta.color}>{statusMeta.label}</Tag>;
          }}
        />
        <Table.Column
          dataIndex="updated_at"
          title="最近更新"
          width={140}
          render={(value?: string) =>
            value ? (
              <Tooltip title={dayjs(value).format("YYYY-MM-DD HH:mm")}>
                {dayjs(value).format("MM-DD HH:mm")}
              </Tooltip>
            ) : (
              "-"
            )
          }
        />
        <Table.Column
          title="操作"
          fixed="right"
          width={140}
          dataIndex="actions"
          render={(_, record: MovieRecord) => (
            <Space>
              <ShowButton hideText size="small" recordItemId={record.id} />
              <EditButton hideText size="small" recordItemId={record.id} />
              <DeleteButton hideText size="small" recordItemId={record.id} />
            </Space>
          )}
        />
      </Table>
    </List>
  );
};
